import { useState } from 'react'
import UserAvatar from '../Shared/UserAvatar'
import { employeeName } from '../../utils/employee'

/** Ícono Font Awesome según el tipo de archivo. */
const fileIcon = (mimeType = '') => {
  if (mimeType.startsWith('image/')) return 'fa-file-image'
  if (mimeType.startsWith('video/')) return 'fa-file-video'
  if (mimeType.startsWith('audio/')) return 'fa-file-audio'
  if (mimeType.includes('pdf')) return 'fa-file-pdf'
  if (mimeType.includes('word') || mimeType.includes('document'))
    return 'fa-file-word'
  if (mimeType.includes('sheet') || mimeType.includes('excel'))
    return 'fa-file-excel'
  if (mimeType.includes('zip') || mimeType.includes('rar'))
    return 'fa-file-zipper'
  return 'fa-file'
}

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatTime = (date) =>
  new Date(date).toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
  })

const formatFull = (date) =>
  new Date(date).toLocaleString('es-CR', {
    dateStyle: 'full',
    timeStyle: 'short',
  })

const isImage = (mimeType) => mimeType?.startsWith('image/')

/** Descarga un archivo (con respaldo de abrirlo en otra pestaña). */
const handleDownload = async (file) => {
  try {
    const response = await fetch(file.url)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = file.name
    a.click()
    URL.revokeObjectURL(blobUrl)
  } catch {
    window.open(file.url, '_blank')
  }
}

/** Resalta las menciones (@Etiqueta) dentro del texto del comentario. */
const renderContent = (text, mentions) => {
  if (!text) return null
  const labels = (Array.isArray(mentions) ? mentions : [])
    .map((m) => m?.label)
    .filter(Boolean)
  if (!labels.length) return text

  const escaped = labels
    .sort((a, b) => b.length - a.length)
    .map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const re = new RegExp(`@(${escaped.join('|')})`, 'g')

  const parts = []
  let last = 0
  let match
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    parts.push(
      <span key={match.index} className="cm-mention">
        @{match[1]}
      </span>
    )
    last = re.lastIndex
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

/**
 * Mensaje del hilo de un ticket, estilo chat: burbuja a la izquierda (otros)
 * o a la derecha (propio), nota interna en ámbar, adjuntos e imágenes con
 * visor. Si continúa un grupo del mismo autor, omite avatar y nombre.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.commentary - Comentario (content, createdAt, internal, mentions).
 * @param {Object} [props.author] - Empleado autor.
 * @param {Array<Object>} [props.files] - Adjuntos del comentario.
 * @param {boolean} [props.isOwn=false] - Si lo escribió el usuario actual.
 * @param {boolean} [props.startsGroup=true] - Si muestra avatar y nombre.
 * @returns {JSX.Element}
 */
const CommentaryTicketCard = ({
  commentary,
  author,
  files = [],
  isOwn = false,
  startsGroup = true,
}) => {
  const [modalImage, setModalImage] = useState(null)

  const imageFiles = files.filter((f) => isImage(f.mimeType))
  const otherFiles = files.filter((f) => !isImage(f.mimeType))
  const isInternal = commentary.internal === true
  const name = employeeName(author) || 'Usuario'

  return (
    <div
      className={`cm ${isOwn ? 'is-own' : ''} ${startsGroup ? 'starts-group' : ''}`}
    >
      <div className="cm-avatar">
        {startsGroup && !isOwn && <UserAvatar employee={author} size={30} />}
      </div>

      <div className="cm-body">
        {startsGroup && (
          <div className="cm-meta">
            <span className="cm-name">{isOwn ? 'Tú' : name}</span>
            {isInternal && (
              <span className="cm-internal-badge">
                <i className="fa-solid fa-lock" />
                Nota interna
              </span>
            )}
          </div>
        )}

        <div className={`cm-bubble ${isInternal ? 'is-internal' : ''}`}>
          {commentary.content && (
            <p className="cm-text">
              {renderContent(commentary.content, commentary.mentions)}
            </p>
          )}

          {imageFiles.length > 0 && (
            <div className="cm-images">
              {imageFiles.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className="cm-image"
                  onClick={() => setModalImage(file)}
                  title={file.name}
                >
                  <img src={file.url} alt={file.name} />
                </button>
              ))}
            </div>
          )}

          {otherFiles.length > 0 && (
            <div className="cm-files">
              {otherFiles.map((file) => (
                <div key={file.id} className="cm-file">
                  <i className={`fa-solid ${fileIcon(file.mimeType)}`} />
                  <span className="cm-file-name" title={file.name}>
                    {file.name}
                  </span>
                  <span className="cm-file-size">{formatSize(file.size)}</span>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cm-file-btn"
                    title="Abrir en otra pestaña"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" />
                  </a>
                  <button
                    type="button"
                    className="cm-file-btn"
                    onClick={() => handleDownload(file)}
                    title="Descargar"
                  >
                    <i className="fa-solid fa-download" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <span className="cm-time" title={formatFull(commentary.createdAt)}>
            {formatTime(commentary.createdAt)}
          </span>
        </div>
      </div>

      {/* Visor de imágenes */}
      {modalImage && (
        <div
          className="cm-viewer"
          role="dialog"
          aria-label={modalImage.name}
          onClick={() => setModalImage(null)}
        >
          <div className="cm-viewer-bar" onClick={(e) => e.stopPropagation()}>
            <span className="text-truncate">{modalImage.name}</span>
            <span className="cm-viewer-size">
              {formatSize(modalImage.size)}
            </span>
            <button
              type="button"
              onClick={() => handleDownload(modalImage)}
              title="Descargar"
            >
              <i className="fa-solid fa-download" />
            </button>
            <a
              href={modalImage.url}
              target="_blank"
              rel="noopener noreferrer"
              title="Abrir en otra pestaña"
            >
              <i className="fa-solid fa-arrow-up-right-from-square" />
            </a>
            <button
              type="button"
              onClick={() => setModalImage(null)}
              title="Cerrar"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <img
            src={modalImage.url}
            alt={modalImage.name}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}

export default CommentaryTicketCard
